/**
 * IntegrationQueueTray — inline queue tray shown above the search/selection area
 * in exporter APs. Shared by CustomerConformityAktionenTab and
 * LieferantenAbfragenAktionenTab.
 *
 * When a queue item is accepted, calls onAccepted(recordIds) so the parent
 * can update its selection state.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconCheck, IconMail, IconWebhook } from "@tabler/icons-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "../../../api/client"

type QueueItem = {
  id: string
  ap_id: string
  label: string
  source: string
  search_query: string
  note: string | null
  created_at: string
  accepted_at: string | null
  rejected_at: string | null
}

function QueueSourceBadge({ source }: { source: string }) {
  const { translate } = useTranslation()
  if (source === "email") {
    return (
      <span className="bg-blue-500/15 text-blue-400 border-blue-500/30 inline-flex shrink-0 items-center gap-0.5 rounded border px-1 py-0.5 text-[10px] font-medium">
        <IconMail size={8} />
        {translate("QMS/QUEUE_TRAY/SOURCE_EMAIL")}
      </span>
    )
  }
  if (source === "webhook") {
    return (
      <span className="bg-purple-500/15 text-purple-400 border-purple-500/30 inline-flex shrink-0 items-center gap-0.5 rounded border px-1 py-0.5 text-[10px] font-medium">
        <IconWebhook size={8} />
        {translate("QMS/QUEUE_TRAY/SOURCE_WEBHOOK")}
      </span>
    )
  }
  if (source === "monitor") {
    return (
      <span className="bg-yellow-500/15 text-yellow-400 border-yellow-500/30 inline-flex shrink-0 items-center gap-0.5 rounded border px-1 py-0.5 text-[10px] font-medium">
        {translate("QMS/QUEUE_TRAY/SOURCE_MONITOR")}
      </span>
    )
  }
  return (
    <span className="shrink-0 rounded border border-grey4 bg-grey3 px-1 py-0.5 text-[10px] font-medium text-grey5">
      {translate("QMS/QUEUE_TRAY/SOURCE_MANUAL")}
    </span>
  )
}

interface IntegrationQueueTrayProps {
  apId: string
  onAccepted: (recordIds: string[]) => void
}

export function IntegrationQueueTray({
  apId,
  onAccepted,
}: IntegrationQueueTrayProps) {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()

  const { data: queueItems = [] } = useQuery<QueueItem[]>({
    queryKey: ["integration-queue", apId],
    queryFn: () => api.integrationQueueList(apId),
    staleTime: 30_000,
  })
  const pendingItems = queueItems.filter(
    (i) => !i.accepted_at && !i.rejected_at,
  )

  const acceptMutation = useMutation({
    mutationFn: (itemId: string) => api.integrationQueueAccept(apId, itemId),
    onSuccess: (result) => {
      const ids = result.customer_ids ?? []
      if (ids.length > 0) onAccepted(ids)
      queryClient.invalidateQueries({ queryKey: ["integration-queue", apId] })
    },
  })

  if (pendingItems.length === 0) return null

  return (
    <div className="space-y-1.5 pb-2">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-grey5">
        {translate("QMS/QUEUE_TRAY/HEADER", { count: pendingItems.length })}
      </p>
      {pendingItems.map((item) => (
        <div
          key={item.id}
          className="flex items-center gap-2 rounded-lg border border-grey4 bg-grey1 px-2.5 py-2 transition-colors hover:border-grey5"
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <QueueSourceBadge source={item.source} />
            <span className="flex-1 truncate text-xs text-white">
              {item.label}
            </span>
            {item.note && (
              <span className="max-w-[120px] shrink-0 truncate text-[11px] text-grey5">
                {item.note}
              </span>
            )}
          </div>
          <button
            onClick={() => acceptMutation.mutate(item.id)}
            disabled={acceptMutation.isPending}
            className="hover:text-green-400 hover:bg-green-400/10 shrink-0 rounded p-1 text-grey5 transition-colors disabled:opacity-40"
            title={translate("QMS/QUEUE_TRAY/TOOLTIP_ACCEPT")}
          >
            <IconCheck size={13} />
          </button>
        </div>
      ))}
    </div>
  )
}
